// subdomain — detect the current tenant slug from the browser hostname.
//
// Rules
//   rheora-us-demo.localhost:5178   → 'rheora-us-demo'
//   rheora-us-demo.example.com      → 'rheora-us-demo'
//   localhost:5178                  → null  (no subdomain)
//   127.0.0.1                       → null  (IP, no subdomain)
//   example.com                     → null  (only one label before TLD)
//
// This is a pure utility (no side-effects) so it is easily unit-tested
// without a browser.

/**
 * Returns the leftmost label of the hostname when it differs from the
 * "bare" host, otherwise null.
 *
 * Dev case: treats `*.localhost` as subdomains of `localhost`.
 * Prod case: treats `<label>.<domain>.<tld>` as having a subdomain.
 * Single-label hosts (IP, `localhost` itself) → null.
 */
export function detectSubdomain(hostname: string): string | null {
  // Strip port if present (hostname already excludes port in Location.hostname,
  // but callers may pass a full host string in tests).
  const host = hostname.split(':')[0]!;

  // IPv4 addresses look like multi-label hostnames but carry no subdomain.
  // A quick digit-only label check is sufficient: all four labels of an IPv4
  // address are numeric (0-255). We check the last label — if it's all digits
  // the host is an IP, not a FQDN, and we return null.
  const labels = host.split('.');
  const lastLabel = labels[labels.length - 1] ?? '';
  if (/^\d+$/.test(lastLabel)) return null;

  // Single label → bare host (localhost, etc.)
  if (labels.length < 2) return null;

  // Two labels ending in "localhost": `rheora-us-demo.localhost` → slug is
  // labels[0].  Plain `localhost` is caught by the single-label rule above.
  if (lastLabel === 'localhost') {
    return labels[0] ?? null;
  }

  // Normal FQDN with ≥ 3 labels: `<slug>.<domain>.<tld>`.
  // Two-label FQDNs (`example.com`) have no subdomain.
  if (labels.length >= 3) {
    return labels[0] ?? null;
  }

  return null;
}

/** Reads the slug from the live browser location. */
export function getCurrentSubdomain(): string | null {
  return detectSubdomain(window.location.hostname);
}
